import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import styles from './index.module.less';
import { IPrize, IWheelConfig } from '@base/common/Wheel/interface';
import cn from 'classnames';
import { isPhone } from 'base/utils/client';

// 默认奖品配置
const defaultPrizes: IPrize[] = [
    { uniqueId: 1, title: "一等奖: 豪华游轮之旅", image: null, color: "#FF6384", displayProbability: 1 },
    { uniqueId: 2, title: "二等奖: 最新款智能手机", image: null, color: "#36A2EB", displayProbability: 1 },
    { uniqueId: 3, title: "三等奖: 品牌平板电脑", image: null, color: "#FFCE56", displayProbability: 1 },
    { uniqueId: 4, title: "四等奖: 蓝牙耳机", image: null, color: "#4BC0C0", displayProbability: 1 },
    {
        uniqueId: 5,
        title: "五等奖: 100元购物卡",
        image: "https://image.next.vivosun.com/asset/width-1110/picture/0768e1ae78e5366b62d40c22d701476d.png",
        color: "#9966FF",
        displayProbability: 1
    },
    { uniqueId: 6, title: "六等奖: 精美纪念品", image: null, color: "#FF9F40", displayProbability: 1 },
];

// 小圆的颜色数组
const Colors = [
    '#05DA54', '#FF5252',
];

const defaultWheelConfig: IWheelConfig = {
    backgroundColor: "#ffffff",
    textColor: "#ffffff",
    fontSize1: 22,
    fontSize2: 14,
    pointerColor: "#05DA54",
    paddingSpace: 10,
    width: "600",
    height: "600",
    smallCircleRadius: 6
}

export type IWheelProps = {
    className?: string;
    prizes: IPrize[];
    wheelConfig?: any;
    onStartSpin?: () => void;
    onEndSpin?: (prize: IPrize) => void;
    onResetWheel?: () => void;
}

export type IWheelRef = {
    start: () => void;
    reset: (index?: number) => void;
    stop: (resultIndex?: number) => void;
}

export const Wheel = React.forwardRef<IWheelRef, IWheelProps>((
    props,
    ref
) => {

    const {
        className,
        prizes = defaultPrizes,
        wheelConfig: wheelConfigProps = defaultWheelConfig,
        onEndSpin,
        onStartSpin,
        onResetWheel,
    } = props

    const wheelConfig = {
        ...defaultWheelConfig,
        ...wheelConfigProps,
    }

    const [spinning, setSpinning] = useState(false);
    const [result, setResult] = useState<IPrize | null>(null);

    // Refs
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const rotationRef = useRef(0);
    const animationRef = useRef<any>(null);
    const resultIndexRef = useRef<number>(-1);
    const targetRotationRef = useRef(0);
    const imageCache = useRef({});
    const spinStartTimeRef = useRef<number>(0);
    const spinPhaseRef = useRef<'accelerating' | 'waiting' | 'decelerating'>('accelerating');

    // 计算总比例
    const totalProportion = prizes.reduce((sum, prize) => sum + prize.displayProbability, 0);

    // 绘制转盘
    const drawWheel = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return
        }

        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const radius = Math.min(centerX, centerY) - wheelConfig.paddingSpace;

        // 清空画布
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // 绘制背景
        if (wheelConfig.backgroundImage) {
            // TODO: 旋转时候有问题 转盘变空白
            const img = new Image();
            img.src = wheelConfig.backgroundImage;
            img.onload = () => {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                drawSegments(ctx, centerX, centerY, radius);
            };
        } else {
            ctx.fillStyle = wheelConfig.backgroundColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            let circleCount = 12;
            let rotationAngle = 0;

            for (let i = 0; i < circleCount; i++) {
                const angle = rotationAngle + (Math.PI * 2 / circleCount) * i;
                const x = centerX + Math.cos(angle) * (radius + 4 - wheelConfig.smallCircleRadius / 2);
                const y = centerY + Math.sin(angle) * (radius + 4 - wheelConfig.smallCircleRadius / 2);

                // 绘制小圆
                ctx.beginPath();
                ctx.arc(x, y, wheelConfig.smallCircleRadius, 0, Math.PI * 2);
                ctx.fillStyle = Colors[i % Colors.length];
                ctx.fill();

                // 添加小圆内部高光
                ctx.beginPath();
                ctx.arc(x - 2, y - 2, wheelConfig.smallCircleRadius / 2, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
                ctx.fill();

                // 绘制连接线
                // ctx.beginPath();
                // ctx.moveTo(centerX, centerY);
                // ctx.lineTo(x, y);
                // ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
                // ctx.lineWidth = 1;
                // ctx.stroke();
            }
            drawSegments(ctx, centerX, centerY, radius);
        }
    };

    // 绘制奖品区域
    const drawSegments = (
        ctx: CanvasRenderingContext2D,
        centerX: number,
        centerY: number,
        _radius: number
    ) => {
        let startAngle = rotationRef.current;
        // console.log({ startAngle })
        let radius = _radius - (2 * wheelConfig.smallCircleRadius) + 4

        // 绘制底座蒙层效果
        ctx.beginPath();
        ctx.arc(centerX, centerY, _radius + wheelConfig.paddingSpace, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0, 0, 0, 0.05)";
        ctx.fill();

        // 绘制阴影效果
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius + wheelConfig.paddingSpace - 5, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0, 0, 0, 0.05)";
        ctx.fill();

        prizes.forEach((prize, index) => {
            const segmentAngle = (prize.displayProbability / totalProportion) * 2 * Math.PI;

            const endAngle = startAngle + segmentAngle;
            const midAngle = startAngle + segmentAngle / 2;

            // 绘制扇形
            ctx.beginPath();
            ctx.moveTo(centerX, centerY);
            ctx.arc(centerX, centerY, radius, startAngle, endAngle);
            ctx.closePath();

            // 设置扇形颜色
            ctx.fillStyle = prize.color;
            ctx.fill();

            // 绘制高光效果
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius * 0.90, startAngle, endAngle);
            ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
            ctx.lineWidth = 2;
            ctx.stroke();

            // 绘制高光效果
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, startAngle, endAngle);
            ctx.strokeStyle = "rgba(255, 255, 255, 1)";
            ctx.lineWidth = 6;
            ctx.stroke();

            // 绘制分割线
            ctx.beginPath();
            ctx.moveTo(centerX, centerY);
            ctx.lineTo(
                centerX + Math.cos(startAngle) * radius,
                centerY + Math.sin(startAngle) * radius
            );
            ctx.strokeStyle = "#fffff";
            if (index == 0) {
                ctx.lineWidth = 4; // 第一个比较细？
            } else {
                ctx.lineWidth = 2;
            }
            ctx.stroke();

            // 保存当前状态
            ctx.save();

            // 移动到圆心
            ctx.translate(centerX, centerY);
            // 旋转到扇区中心
            ctx.rotate(midAngle + Math.PI / 2);

            // 计算文本和图片位置
            const textRadius = radius * 0.6;
            const maxTextWidth = segmentAngle * textRadius * (isPhone ? 0.8 : 0.9); // 最大文本宽度基于扇区角度

            // 设置文本样式
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillStyle = wheelConfig.textColor;

            // 文本阴影
            ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;

            // 绘制奖品图片
            if (prize.image) {
                // 使用缓存避免重复加载
                if (!imageCache.current[prize.uniqueId]) {
                    const img = new Image();
                    img.src = prize.image;
                    img.onload = () => {
                        imageCache.current[prize.uniqueId] = img;
                        drawWheel(); // 图片加载后重绘
                    };
                } else {
                    const img = imageCache.current[prize.uniqueId];
                    const imgSize = Math.min(radius * 0.4, textRadius * 0.8);
                    // 图片位置 - 在扇区中心线上方
                    ctx.drawImage(
                        img,
                        -imgSize / 2,
                        -textRadius - 50,
                        imgSize,
                        imgSize
                    );
                }
            } else {
                // 绘制图片占位符
                ctx.beginPath();
                ctx.arc(0, -textRadius - 35, 7, 0, Math.PI * 2);
                ctx.fillStyle = wheelConfig.textColor || "rgba(255, 255, 255, 0.3)";
                ctx.fill();
            }

            // 文本换行处理函数
            const wrapText = (
                text: string,
                maxWidth: number,
                params?: {
                    isTitle1?: boolean;
                    isSpace?: boolean
                },
            ) => {
                if (!text) {
                    return []
                }
                const { isSpace = true, isTitle1 } = params || {};
                // title 1 每个字判断， title2 单词（空格拆分）判断
                const replaceSymbol = isSpace ? ' ' : '';
                if (isTitle1) {
                    ctx.font = `500 ${wheelConfig.fontSize1 || 16}px Arial`;
                } else {
                    ctx.font = `400 ${wheelConfig.fontSize2 || 12}px Arial`;
                }

                const words = text.split(replaceSymbol);
                const lines: string[] = [];
                let currentLine = words[0];

                for (let i = 1; i < words.length; i++) {
                    const word = words[i];
                    const testLine = currentLine + replaceSymbol + word;
                    const metrics = ctx.measureText(testLine);
                    if (metrics.width <= maxWidth) {
                        currentLine = testLine;
                    } else {
                        lines.push(currentLine);
                        currentLine = word;
                    }
                }
                lines.push(currentLine);
                return lines;
            };

            // 获取换行后的文本
            const lines2 = wrapText(prize.title2 || '', maxTextWidth);
            const lines1 = wrapText(prize.title, maxTextWidth, {
                isTitle1: true,
                isSpace: !(lines2.length > 0),
            });
            const lineHeight1 = wheelConfig.fontSize1 + 2;
            const lineHeight2 = wheelConfig.fontSize2 + 2;

            // 计算文本起始位置
            const textStartY = -textRadius + 4 + (prize.image ? 10 : -12);

            // 绘制多行文本
            // title 2 换行绘制
            ctx.font = `400 ${wheelConfig.fontSize2 || 12}px Arial`;
            for (let i = 0; i < lines2.length; i++) {
                // title2 向下换行
                if (!!lines2[i]) {
                    ctx.fillText(lines2[i], 0, textStartY + 5 + ((i + 1) * lineHeight2));
                }
            }

            // title 1 绘制 （一行省略）
            ctx.font = `500 ${wheelConfig.fontSize1 || 16}px Arial`;
            if (lines2.length > 0) {
                ctx.fillText(`${lines1[0]}${lines1.length > 1 ? '..' : ''}`, 0, textStartY);
            } else {
                for (let i = 0; i < lines1.length; i++) {
                    // title1 向上占行
                    if (!!lines1[i]) {
                        ctx.fillText(lines1[i], 0, textStartY + (i * lineHeight1));
                    }
                }
            }

            // 恢复状态
            ctx.restore();

            // 更新起始角度
            startAngle = endAngle;
        });

        // 绘制中心点
        ctx.beginPath();
        ctx.arc(centerX, centerY, 18, 0, 2 * Math.PI);
        ctx.fillStyle = "#fff";
        ctx.fill();

        // 中心点阴影
        ctx.beginPath();
        ctx.arc(centerX, centerY, 18, 0, 2 * Math.PI);
        ctx.strokeStyle = "rgba(0, 0, 0, 0.1)";
        ctx.lineWidth = 2;
        ctx.stroke();

        // 中心点装饰
        ctx.beginPath();
        ctx.arc(centerX, centerY, 15, 0, 2 * Math.PI);
        ctx.fillStyle = "rgba(20, 195, 85, 1)";
        // ctx.fillStyle = wheelConfig.pointerColor;
        ctx.fill();
    };

    // 计算目标角度（确保指针指向中奖区域）
    const calculateTargetAngle = () => {
        // 计算中奖奖品在转盘上的位置
        let prizeAngle = 0;
        for (let i = 0; i < resultIndexRef.current; i++) {
            prizeAngle += (prizes[i].displayProbability / totalProportion) * 2 * Math.PI;
        }
        // 添加奖品中心点偏移
        const segmentAngle = (prizes[resultIndexRef.current].displayProbability / totalProportion) * Math.PI;
        prizeAngle += segmentAngle;

        // 计算目标角度（5圈 + 调整角度）
        targetRotationRef.current =
            (5 * 2 * Math.PI) + // 至少转5圈
            (Math.PI - prizeAngle) + // 调整到指针位置
            (Math.PI / 2); // 补偿初始角度偏移

        // console.log(targetRotationRef.current);
    };

    // 重置转盘
    const resetWheel = (index: number = -1) => {
        setResult(null);
        if (index > -1) {
            resultIndexRef.current = index;
            calculateTargetAngle();
            rotationRef.current = targetRotationRef.current;
        } else {
            resultIndexRef.current = -1;
            rotationRef.current = 0;
        }

        drawWheel();
    };

    // 开始旋转动画
    const startSpin = () => {
        if (spinning) return;

        setSpinning(true);
        setResult(null);
        spinStartTimeRef.current = 0;
        spinPhaseRef.current = 'accelerating';

        // 开始动画
        animationRef.current = requestAnimationFrame(animate);

        onStartSpin?.()
    };

    const stopSpin = (resultIndex?: number) => {
        resultIndexRef.current = resultIndex || 0;
        calculateTargetAngle();
        // 切换到减速阶段
        spinPhaseRef.current = 'decelerating';
        spinStartTimeRef.current = performance.now(); // 重置计时器用于减速阶段
    }


    // 动画函数
    const animate = (timestamp: number) => {
        if (!spinStartTimeRef.current) {
            spinStartTimeRef.current = timestamp;
        }

        const elapsed = timestamp - spinStartTimeRef.current;

        switch (spinPhaseRef.current) {
            case 'accelerating':
                // todo: 加速阶段：前2秒加速旋转
                const accelerationProgress = Math.min(elapsed / 2000, 1);
                const accelerationFactor = 1 - Math.pow(accelerationProgress, 3);
                // const accelerationFactor = 1 - Math.pow(1- accelerationProgress, 3);
                rotationRef.current = -accelerationFactor * 10 * Math.PI; // 加速旋转5圈

                // console.log({ 'accelerating': rotationRef.current } )

                if (accelerationProgress >= 1) {
                    spinPhaseRef.current = 'waiting'; // 切换到等待结果阶段
                }
                break;

            case 'waiting':
                // 等待结果阶段：保持匀速旋转
                rotationRef.current += 0.3; // 匀速旋转
                break;

            case 'decelerating':
                // 减速阶段：API返回后开始减速
                const decelerationProgress = Math.min(elapsed / 5000, 1);
                const decelerationFactor = 1 - Math.pow(1 - decelerationProgress, 4);
                rotationRef.current = targetRotationRef.current * decelerationFactor;

                if (decelerationProgress >= 1) {
                    // 动画结束
                    setSpinning(false);

                    const _result = prizes[resultIndexRef.current]
                    setResult(_result);

                    onEndSpin?.(_result);
                    return; // 停止动画循环
                }
                break;
        }
        // 重绘转盘
        drawWheel();
        // 继续动画
        animationRef.current = requestAnimationFrame(animate);
    };


    useImperativeHandle(ref, () => ({
        start: startSpin,
        reset: resetWheel,
        stop: stopSpin,
    }))


    // 组件挂载和更新时绘制转盘
    useEffect(() => {
        drawWheel();
    }, [prizes, wheelConfig]);

    // 组件卸载时取消动画
    useEffect(() => {
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    // 处理窗口大小变化
    useEffect(() => {
        const handleResize = () => {
            drawWheel();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div
            className={cn(
                styles['g-wheel-warp'],
                className,
                "wheel-wrap"
            )}
        >
            <canvas
                ref={canvasRef}
                width={wheelConfig.width}
                height={wheelConfig.height}
                style={{ maxWidth: '100%', height: 'auto' }}
            />

            <div
                className="g-pointer"
                style={{ backgroundColor: wheelConfig.pointerColor }}
            />
        </div>
    );
});
